import { useCallback, useRef, useState } from 'react'
import { useAppStore } from '@store/appStore'
import type { ShopMetadata } from '@app-types/shopMetadata'
import { imageCache } from '@services/imageCache'
import { getShopCardPreviewAnimationService, AnimationFrameCallback } from '@services/frameAnimationService'
import { loadManifestStreaming, buildFrameUrls } from '@utils/manifestStreaming'
import { resolveUrl } from '../config'

/**
 * Hook for shop card preview animation functionality
 * Provides diagnostic logging and frame preview capabilities
 * Uses manifest.json with streaming for fast frame discovery
 */

/**
 * Find best section by score (highest first) and frame count (most frames)
 */
function findBestSection(sections: any[]): any | null {
  if (!sections || sections.length === 0) return null
  
  return sections.sort((a, b) => {
    // Sort by score descending
    const scoreDiff = (b.score ?? 0) - (a.score ?? 0)
    if (scoreDiff !== 0) return scoreDiff
    
    // Then by frame count descending
    return (b.frame_count ?? 0) - (a.frame_count ?? 0)
  })[0]
}

/**
 * Load frames from manifest.json and return URLs
 * Replaces frame discovery which was doing 100+ HEAD requests
 */
async function loadManifestFrames(
  manifestPath: string,
  basePath: string,
  shopId: string
): Promise<{urls: string[], frameCount: number, loadTimeMs: number} | null> {
  const result = await loadManifestStreaming(manifestPath)
  if (!result.framesIndex || result.framesIndex.length === 0) {
    console.warn(`[useShopCardPreview] No frames found in manifest at ${manifestPath}`)
    return null
  }

  const urls = buildFrameUrls(result.framesIndex, basePath, shopId)
  return {
    urls,
    frameCount: result.frameCount,
    loadTimeMs: result.loadTimeMs,
  }
}

export function useShopCardPreview() {
  const availableDatasets = useAppStore((state) => state.availableDatasets)
  
  // Preview animation state
  const [previewFrame, setPreviewFrame] = useState<string | null>(null)
  const [previousFrame, setPreviousFrame] = useState<string | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [isFading, setIsFading] = useState(false)
  const [previewEffects, setPreviewEffects] = useState<{ currentOpacity?: number; previousOpacity?: number }>({})
  const animationServiceRef = useRef(getShopCardPreviewAnimationService())
  const currentShopRef = useRef<string | null>(null)
  const failedShopsRef = useRef<Set<string>>(new Set()) // Track shops with failed loads
  const failureCooldownRef = useRef<Map<string, NodeJS.Timeout>>(new Map())

  // Get dataset info for a shop
  const getDatasetInfo = useCallback((shopId: string) => {
    return availableDatasets.find(d => d.id === shopId)
  }, [availableDatasets])



  // Stop preview animation
  const stopPreview = useCallback(() => {
    console.log(`🛑 [useShopCardPreview] Stopping preview`)
    
    if (animationServiceRef.current && (animationServiceRef.current as any).cancelPreview) {
      ;(animationServiceRef.current as any).cancelPreview()
    }

    // Reset state immediately to prevent contamination during transitions
    setIsPreviewing(false)
    setPreviewFrame(null)
    setPreviousFrame(null)
    currentShopRef.current = null

    // Fade out the preview image (keep it in DOM for CSS transition)
    setIsFading(true)
    
    // After transition completes, clear fading flag
    setTimeout(() => {
      setIsFading(false)
    }, 300) // Match CSS transition duration
  }, [])

  // Start preview animation for a shop
  const startPreview = useCallback(
    (shopId: string, shopMetadata: Map<string, ShopMetadata>) => {
      // Check if this shop is in cooldown after a failed load
      if (failedShopsRef.current.has(shopId)) {
        console.log(`⏳ [useShopCardPreview] Shop ${shopId} in failure cooldown, skipping`)
        return
      }

      console.log(`🎬 [useShopCardPreview] startPreview called for ${shopId}`, {
        isPreviewing,
        currentShop: currentShopRef.current,
        metadataSize: shopMetadata.size
      })

      // Always stop any existing preview first
      if (animationServiceRef.current && (animationServiceRef.current as any).cancelPreview) {
        ;(animationServiceRef.current as any).cancelPreview()
      }
      
      // Reset state completely to prevent data from previous shop bleeding through
      setIsPreviewing(false)
      setPreviewFrame(null)
      setPreviousFrame(null)
      currentShopRef.current = null

      const metadata = shopMetadata.get(shopId)
      if (!metadata || !metadata.sections || metadata.sections.length === 0) {
        console.warn(`🎬 [useShopCardPreview] No metadata for shop: ${shopId}`)
        return
      }

      // Find best section by score
      const bestSection = findBestSection(metadata.sections)
      if (!bestSection) {
        console.warn(`🎬 [useShopCardPreview] No sections available for shop: ${shopId}`)
        return
      }

      // Get base path from dataset configuration to support folder organization (e.g., NewShops/)
      const dataset = availableDatasets.find(d => d.id === shopId)
      const datasetBasePath = dataset?.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shopId}`
      
      const basePath = resolveUrl(bestSection.paths?.frames || `${datasetBasePath}/section_${bestSection.section_id}/frames`)
      const manifestPath = resolveUrl(bestSection.paths?.manifest || `${datasetBasePath}/section_${bestSection.section_id}/manifest.json.gz`)

      console.log(`🎬 [useShopCardPreview] Starting preview for ${shopId} (using section with score ${bestSection.score})`)
      
      currentShopRef.current = shopId
      setIsPreviewing(true)

      // Load frames from manifest instead of discovering with HEAD requests
      loadManifestFrames(manifestPath, basePath, shopId)
        .then((manifestResult) => {
          if (!manifestResult || manifestResult.urls.length === 0) {
            console.warn(`🎬 [useShopCardPreview] No frames loaded from manifest for shop: ${shopId}`)
            stopPreview()
            return
          }

          const actualFramePaths = manifestResult.urls
          console.log(`🎬 [useShopCardPreview] Loaded ${actualFramePaths.length} frames from manifest in ${manifestResult.loadTimeMs}ms for ${shopId}`)
          console.log(`🎬 [useShopCardPreview] First 3 frame URLs:`, actualFramePaths.slice(0, 3))
          
          // Preload first few frames - allow individual failures
          const preloadPromises = actualFramePaths.slice(0, Math.min(3, actualFramePaths.length)).map(path => {
            console.log(`🎬 [useShopCardPreview] Preloading: ${path}`)
            return imageCache.preload(path)
              .catch(_err => {
                console.warn(`⚠️ [useShopCardPreview] Frame failed to load (will skip): ${path}`)
                return null // Return null instead of rejecting
              })
          })
          
          Promise.all(preloadPromises).then(() => {
            // Create animation frames (indices into actualFramePaths array)
            const frameIndices = Array.from({ length: actualFramePaths.length }, (_, i) => i)
            
            // Define animation config so callbacks can reference it (closure)
            const config = {
              frameDuration: 200, // 200ms per frame for snappy preview
              frameSamplingRate: 1, // Show ALL frames (1 = no skipping)
              zIndex: 1000, // Above shop card but below modal
              opacity: 0.9,
              borderColor: '#00FF00', // Green border for shop preview
              label: 'ShopCardPreview',
              effects: {
                fadeEnvelope: { frameWindow: 15 } // Spread cross-fade over 15 frames
              }
            }
            
            const animationCallbacks: AnimationFrameCallback = {
              onFrameShow: (frameIndex: number) => {
                // Only update state if we're still previewing this shop
                // (prevents stale callbacks from previous shop from contaminating state)
                if (currentShopRef.current !== shopId) {
                  console.log(`⚠️ [useShopCardPreview] Ignoring frame callback for ${shopId}, now previewing ${currentShopRef.current}`)
                  return
                }
                
                const framePath = actualFramePaths[frameIndex]
                // Store previous frame before updating current
                // On first frame, preview fades out as animation fades in
                setPreviewFrame(prevFrame => {
                  setPreviousFrame(prevFrame)
                  return framePath
                })
              },
              onComplete: () => {
                // Animation complete - stop preview immediately
                console.log(`✅ [useShopCardPreview] Preview animation completed for ${shopId}`)
                stopPreview()
              },
              onEffectsUpdate: (progress: number, frameIndex: number, totalFrames: number) => {
                // Per-frame cross-fade: spread effect over frameWindow frames
                if (!config.effects?.fadeEnvelope) return

                const { frameWindow } = config.effects.fadeEnvelope

                let currentOpacity = 1.0
                let previousOpacity = 0.0

                if (frameIndex < frameWindow) {
                  // Fade in: first frameWindow frames
                  currentOpacity = frameIndex / frameWindow
                  previousOpacity = 1 - (frameIndex / frameWindow)
                } else if (frameIndex > totalFrames - frameWindow) {
                  // Fade out: last frameWindow frames
                  const framesFromEnd = totalFrames - frameIndex
                  currentOpacity = framesFromEnd / frameWindow
                  previousOpacity = 1 - (framesFromEnd / frameWindow)
                } else {
                  // Middle: full opacity, no cross-fade
                  currentOpacity = 1
                  previousOpacity = 0
                }

                setPreviewEffects({ currentOpacity, previousOpacity })
              }
            }

            // Start animation with slow, smooth playback
            const cancelAnimation = animationServiceRef.current.animateFrameSequence(
              frameIndices,
              frameIndices.length - 1, // Play to last frame, don't loop
              config,
              animationCallbacks
            )

            // Store cancel function for cleanup
            ;(animationServiceRef.current as any).cancelPreview = cancelAnimation
          }).catch(err => {
            console.error(`🎬 [useShopCardPreview] Failed to preload frames for ${shopId}:`, err)
            stopPreview()
          })
        })
        .catch(err => {
          console.error(`🎬 [useShopCardPreview] Failed to load frames from manifest for ${shopId}:`, err)
          
          // Add to failed shops and set 5-second cooldown
          failedShopsRef.current.add(shopId)
          
          // Clear any existing cooldown timer
          const existingTimer = failureCooldownRef.current.get(shopId)
          if (existingTimer) {
            clearTimeout(existingTimer)
          }
          
          // Set new 5-second cooldown
          const timer = setTimeout(() => {
            failedShopsRef.current.delete(shopId)
            failureCooldownRef.current.delete(shopId)
            console.log(`✅ [useShopCardPreview] Cooldown expired for ${shopId}, can retry`)
          }, 5000)
          
          failureCooldownRef.current.set(shopId, timer)
          
          stopPreview()
        })
    },
    [isPreviewing, stopPreview]
  )

  // 🔍 Diagnostic: Log all data needed for shop card preview animation
  const logShopData = useCallback((shopId: string, shopMetadata: Map<string, ShopMetadata>) => {
    console.log(`\n════════════════════════════════════════════════════════════════`)
    console.log(`📊 [useShopCardPreview] DIAGNOSTIC LOG FOR SHOP: ${shopId}`)
    console.log(`════════════════════════════════════════════════════════════════\n`)

    // 1. Dataset info
    const datasetInfo = getDatasetInfo(shopId)
    console.log('1️⃣  Dataset Info:')
    console.log(`   - Exists: ${!!datasetInfo}`)
    console.log(`   - Name: ${datasetInfo?.name}`)
    console.log(`   - Is Multi-Section: ${datasetInfo?.is_multi_section}`)
    console.log(`   - Camera Count: ${datasetInfo?.camera_count}`)

    // 2. Shop metadata
    const metadata = shopMetadata.get(shopId)
    console.log('\n2️⃣  Shop Metadata:')
    console.log(`   - Loaded: ${!!metadata}`)
    if (metadata) {
      console.log(`   - Total Sections: ${metadata.total_sections}`)
      console.log(`   - Total Frames: ${metadata.total_frames}`)
      console.log(`   - Total Cameras: ${metadata.total_cameras}`)
      console.log(`   - Generated At: ${metadata.generated_at}`)
    }

    // 3. First section details
    console.log('\n3️⃣  First Section (section_0):')
    if (metadata && metadata.sections && metadata.sections.length > 0) {
      const firstSection = metadata.sections[0]
      console.log(`   - Section ID: ${firstSection.section_id}`)
      console.log(`   - Name: ${firstSection.name}`)
      console.log(`   - Frame Range: [${firstSection.frame_range[0]}, ${firstSection.frame_range[1]}]`)
      console.log(`   - Frame Count: ${firstSection.frame_count}`)
      console.log(`   - Camera Count: ${firstSection.camera_count}`)
      console.log(`   - Frames Path: ${firstSection.paths?.frames}`)

      // 4. Example frame paths
      console.log('\n4️⃣  Example Frame Paths:')
      // Get base path from dataset configuration
      const dataset = availableDatasets.find(d => d.id === shopId)
      const datasetBasePath = dataset?.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shopId}`
      const sectionFramePath = resolveUrl(firstSection.paths?.frames || `${datasetBasePath}/frames`)
      const frame0Path = `${sectionFramePath}/frame_0000.jpg?shop=${shopId}`
      const frame1Path = `${sectionFramePath}/frame_0001.jpg?shop=${shopId}`
      console.log(`   - Frame 0: ${frame0Path}`)
      console.log(`   - Frame 1: ${frame1Path}`)

      // 5. Verify first frame exists (fetch metadata)
      console.log('\n5️⃣  Frame Existence Check:')
      fetch(frame0Path, { method: 'HEAD' })
        .then((res) => {
          console.log(`   ✅ frame_0000.jpg exists (HTTP ${res.status})`)
        })
        .catch((err) => {
          console.log(`   ❌ frame_0000.jpg fetch failed: ${err.message}`)
        })

      // 6. All sections summary
      console.log('\n6️⃣  All Sections:')
      metadata.sections.forEach((_section, _idx) => {
        console.log(`   Section ${_idx}: ID=${_section.section_id}, frames=${_section.frame_count}, range=[${_section.frame_range[0]}-${_section.frame_range[1]}]`)
      })
    } else {
      console.log(`   ❌ No metadata loaded yet`)
    }

    console.log(`\n════════════════════════════════════════════════════════════════\n`)
  }, [getDatasetInfo])

  return {
    // Diagnostic functions
    logShopData,
    getDatasetInfo,

    // Preview animation functions
    previewFrame,
    previousFrame,
    previewEffects,
    isPreviewing,
    isFading,
    currentShopRef, // Export currentShopRef so component can check which shop is being previewed
    startPreview,
    stopPreview
  }
}